import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { SortingState } from "@tanstack/react-table";
import { Link } from "react-router-dom";

import { contactsApi } from "../api/contacts";
import type { Contact } from "../types";
import { PageHeader } from "../components/PageHeader";
import { QueryBoundary } from "../components/QueryBoundary";

const columnHelper = createColumnHelper<Contact>();

const columns = [
  columnHelper.accessor(
    (row) => `${row.data.personal.first_name} ${row.data.personal.last_name}`,
    {
      id: "name",
      header: "Name",
      cell: (ctx) => (
        <Link
          to={`/contacts/${ctx.row.original.id}`}
          className="font-heading font-semibold text-ink hover:text-aqua-medium"
        >
          {ctx.getValue() as string}
        </Link>
      ),
    },
  ),
  columnHelper.accessor((row) => row.data.personal.email, {
    id: "email",
    header: "Email",
    cell: (c) => (
      <span className="text-sm text-ink-low">
        {(c.getValue() as string | undefined) || "—"}
      </span>
    ),
  }),
  columnHelper.accessor(
    (row) =>
      row.data.professional?.company_name || row.data.professional?.company_id,
    {
      id: "company",
      header: "Company",
      cell: (c) => (
        <span className="text-sm text-ink-medium">
          {(c.getValue() as string | undefined) || "—"}
        </span>
      ),
    },
  ),
  columnHelper.accessor((row) => row.data.memberships?.campaigns?.length ?? 0, {
    id: "memberships",
    header: "Campaigns",
    cell: (c) => {
      const n = c.getValue() as number;
      return (
        <span className={`tnum font-medium ${n > 1 ? "text-aqua-medium" : "text-ink-low"}`}>
          {n}
        </span>
      );
    },
  }),
];

export function ContactsPage() {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 200);
    return () => clearTimeout(t);
  }, [q]);

  const query = useQuery({
    queryKey: ["contacts", debounced],
    queryFn: () => contactsApi.list({ q: debounced || undefined, limit: 200 }),
  });

  const table = useReactTable({
    data: query.data ?? [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="page">
      <PageHeader
        title="Contacts"
        description="Searchable list of people in the CRM — click a name for full detail, campaigns and activity."
      />

      <div className="flex items-end gap-4 mb-5">
        <div className="flex-1 max-w-md">
          <label className="label" htmlFor="contacts-search">
            Search
          </label>
          <input
            id="contacts-search"
            className="input"
            placeholder="by name or slug…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="text-sm text-ink-low pb-2">
          <span className="tnum font-medium text-ink">{query.data?.length ?? 0}</span>{" "}
          listed
        </div>
      </div>

      <QueryBoundary query={query}>
        {() => (
          <div className="panel panel--flush overflow-x-auto">
            <table className="table">
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((h) => (
                      <th
                        key={h.id}
                        className="cursor-pointer select-none"
                        onClick={h.column.getToggleSortingHandler()}
                      >
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getIsSorted() === "asc" && (
                          <span className="ml-1 text-aqua-medium">↑</span>
                        )}
                        {h.column.getIsSorted() === "desc" && (
                          <span className="ml-1 text-aqua-medium">↓</span>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>
    </div>
  );
}
